import { getTranslatedText, ENTITY_MAP, ALL_MODULES } from '@nile/components';
import {
  AccountClient,
  getApiMethodAndKeyNames,
  apiResolver,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA,
  RMMClient,
  GET_CLIENT_USER_LIST,
  GET_FIELD,
  GET_CLIENT_USERS
} from '@nile/graphql';
import { GET_ASSET_LIST_FOR_DROPDOWN } from '@nile/graphql/src/Query/ticket';
import { platformCategoryOptions } from '@nile/rmm/src/modules/asset-management/components/constants/asset-rule-builder';
import { includes } from 'lodash';

function getQueryVars(item: string, searchInput: string, clientId: string) {
  switch (item) {
    case 'name':
      return {
        query: GET_ASSET_LIST_FOR_DROPDOWN,
        dataLevel: 'getWorkStationList.assets',
        valueLevel: 'name',
        client: RMMClient,
        variables: {
          listInfo: {
            condition: {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'name',
                  operator: 'contains',
                  value: searchInput
                },
                clientId && {
                  attribute: 'client.accountId',
                  operator: 'is',
                  value: clientId
                }
              ].filter(Boolean)
            }
          }
        }
      };

    case 'assetOwner.name':
      return {
        query: GET_CLIENT_USERS,
        dataLevel: 'getClientUserAssociationsList.associations',
        valueLevel: 'user.name',
        client: AccountClient,
        variables: {
          input: {
            listInfo: {
              condition: {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'user.name',
                    operator: 'contains',
                    value: searchInput
                  }
                ]
              }
            },
            ...(clientId && { clientId })
          }
        }
      };
  }

  return null;
}

/**
 * For entity type, if we want to call a specif api, this method will be called.
 * @param {object} item
 */
function getQuery(item: { name: string }) {
  const entity = item?.name?.split('.')[0] || '';
  switch (entity) {
    case 'technician':
    case 'followers':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

function formatOperatorValues(allOperators: any, operator: string, intl: any) {
  return allOperators.getRuleBuilderOperators.operator_placeholders[
    operator
  ].map((el: { key: string }) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.key),
      value: getTranslatedText(intl, el.key),
      name: el.key
    };
  });
}

/**
 * This method gives the query and client using which the API calls will be made
 * after selecting the attribute
 * @param {object} item
 * @param {String} operator
 */

function getPropValues(
  item: any,
  operator: string,
  allOperators: any,
  intl: any,
  clientId: string
) {
  const listInfo = {
    sort: [
      {
        attribute: 'name',
        order: 'ASC'
      }
    ],
    ...(clientId && {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            value: clientId,
            operator: 'is',
            attribute: 'client.accountId'
          }
        ]
      }
    })
  };

  if (item?.name === 'assetClass.name') {
    if (listInfo.condition) {
      listInfo.condition.operands.push({
        value: ['ASSET', 'UN_MONITORED_ASSET'],
        operator: 'includes',
        attribute: 'assetClass.moduleType'
      });
    } else {
      listInfo.condition = {
        joinOperator: 'AND',
        operands: [
          {
            value: ['ASSET', 'UN_MONITORED_ASSET'],
            operator: 'includes',
            attribute: 'assetClass.moduleType'
          }
        ]
      };
    }
  }

  if (item.name === 'monitoringStatus') {
    return [
      {
        label: 'Supported',
        name: 'SUPPORTED',
        value: 'SUPPORTED'
      },
      {
        label: 'Not Supported',
        name: 'NOT_SUPPORTED',
        value: 'NOT_SUPPORTED'
      }
    ];
  }

  if (item.name === 'platformCategory') {
    return platformCategoryOptions;
  }

  if (item.isCustom) {
    if (includes(['date', 'datetime'], item.dataType) && operator) {
      return formatOperatorValues(allOperators, operator, intl);
    }
    let module = 'asset';
    let columnName = item?.name || item?.columnName;
    if (item.name.split('.')?.length > 1) {
      module = item.name.split('.').shift();
      columnName = item.name.split('.').pop();
    }
    return AccountClient.query({
      query: GET_FIELD,
      variables: {
        input: {
          columnName,
          // module: ALL_MODULES.asset,
          entity: module
        }
      }
    });
  }
  if (item?.name === 'owner.name') {
    return AccountClient.query({
      query: GET_CLIENT_USER_LIST,
      variables: {
        input: {
          listInfo: {
            ...listInfo,
            sort: [
              {
                attribute: 'user.name',
                order: 'ASC'
              }
            ]
          }
        }
      }
    });
  }
  if (item?.name) {
    const query = apiResolver.Asset[item.name]?.api;
    const client = apiResolver.Asset[item.name]?.client;
    let variables = apiResolver.Asset[item.name]?.variables || {};

    if (query && client) {
      return client.query({
        query: query,
        variables: {
          input: listInfo
        }
      });
    }
    if (operator) {
      return formatOperatorValues(allOperators, operator, intl);
    }
    return { client: AccountClient, query: getQuery(item) };
  }
  return {};
}

async function onAttributeSelect(params, clientId) {
  const { item: field, operator, fields, allOperators, intl } = params;
  const allFields = fields;
  const selectedField = allFields.find((el) => el.name === field);

  if (selectedField?.name === 'monitoringStatus') {
    return [
      {
        label: 'Supported',
        name: 'SUPPORTED',
        value: 'SUPPORTED'
      },
      {
        label: 'Not Supported',
        name: 'NOT_SUPPORTED',
        value: 'NOT_SUPPORTED'
      }
    ];
  }

  if (selectedField?.name === 'platformCategory') {
    return platformCategoryOptions;
  }

  if (selectedField?.dataType === 'boolean') {
    return [
      {
        name: 'true',
        label: 'true',
        value: true
      },
      {
        name: 'false',
        label: 'false',
        value: false
      }
    ];
  }
  const allValues = await getPropValues(
    selectedField,
    operator,
    allOperators,
    intl,
    clientId
  );
  let res = [];

  if (selectedField?.name === 'owner.name') {
    return allValues.data?.getClientUserAssociationsList?.associations
      ? allValues.data?.getClientUserAssociationsList.associations.map((el) => {
          return {
            name: el.user.name,
            value: el.user.name,
            label: el.user.name
          };
        })
      : [];
  }

  if (selectedField?.name) {
    const apiMethodAndKeyNames = getApiMethodAndKeyNames(ENTITY_MAP.Asset);
    let method = apiMethodAndKeyNames[selectedField.name]?.method;
    const key = apiMethodAndKeyNames[selectedField.name]?.key;
    const secondaryMethod =
      apiMethodAndKeyNames[selectedField.name]?.secondaryMethod;
    const nestedKey = apiMethodAndKeyNames[selectedField.name]?.nestedKey;

    let dataToMap = null;
    if (secondaryMethod) {
      dataToMap = allValues.data[method]?.[secondaryMethod];
    } else if (operator === 'on') {
      dataToMap = allValues;
    } else {
      dataToMap = allValues.data?.[method];
    }
    if (selectedField.nestedTo) {
      const nestedProp = apiMethodAndKeyNames[selectedField.name]?.nestedKey;
      if (!dataToMap && !method) {
        dataToMap = allValues.data?.getField?.options;
      }
      dataToMap.forEach((el) => {
        if (el[nestedProp]) {
          res = res.concat(
            el[nestedProp].map((c) => {
              return {
                name: c[key],
                value: c[key],
                label: c[key]
              };
            })
          );
        } else {
          res = res.concat([
            { name: el?.value, value: el?.value, label: el?.value }
          ]);
        }
      });
    } else if (operator === 'on') {
      res = allValues;
    } else if (selectedField.isCustom) {
      method = 'getField';
      const fldOptions =
        allValues?.data?.[method]?.options ||
        allValues?.data?.[method]?.fieldOptions;
      res = allValues?.data
        ? fldOptions?.map((el) => {
            return {
              name: el?.value,
              value: el?.value,
              label: el?.value
            };
          })
        : [];
    } else if (nestedKey) {
      res = dataToMap?.[nestedKey]
        ? dataToMap?.[nestedKey].map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    } else {
      res = dataToMap
        ? dataToMap.map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    }
  }
  return res;
}

/**
 * This method is called for autocomplete fields.
 * Usually apis will be called when we choose attributes. But for autocomplete,
 * on typing few characters, api will be called. This is the method.
 * @param {object} client
 * @param {gql} query
 * @param {object} params
 */
const onValueSearch = async (__, _, params, clientId) => {
  const values = getQueryVars(params.field, params.inputValue, clientId);

  if (!values) return;

  const { query, dataLevel, valueLevel, client, variables } = values;

  const res = await client.query({
    query,
    variables
  });

  const value = getNestedValue(dataLevel, res?.data);

  const allOptions = value
    ? value.map((el) => {
        const itemVal = getNestedValue(valueLevel, el);
        return {
          name: itemVal,
          value: itemVal,
          label: itemVal
        };
      })
    : [];
  return allOptions;
};

function getNestedValue(key, data) {
  if (!data) return null;

  return key.split('.').reduce((acc, curr) => {
    if (!acc) return {};
    return acc[curr];
  }, data);
}

export { onAttributeSelect, onValueSearch };
